import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import VideoPlayer from '../Components/VideoPlayer';
import API from '../api';

const Watch = () => {
  const { type, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ✅ If navigated with state (from All.js handleWatch), use directly
    if (location.state?.item) {
      const item = location.state.item;
      // Prefer passed url, then video_url, then stream_url, then youtube_embed
      const resolvedUrl =
        location.state.url ||
        item.video_url ||
        item.stream_url ||
        item.youtube_embed ||
        null;
      setVideoData({ ...item, video_url: resolvedUrl });
      if (item.image) sessionStorage.setItem(`video_${id}_image`, item.image);
      setLoading(false);
      return;
    }

    // ✅ Fallback: fetch from API
    const fetchVideoData = async () => {
      try {
        const BASE = `${API}/api/movie_streaming`;

        // All sections to search
        const sections = [
          type === 'movie' ? 'movies' : 'series',
          'latest_trending',
          'continue_watching',
          'top_10_india',
          'watchlist',
          'live_tv',
          'recently_added',
          'imdb_top_picks',
          'hero_slides',
        ];

        for (const section of sections) {
          try {
            const res = await fetch(`${BASE}/${section}`);
            if (!res.ok) continue;
            const data = await res.json();
            const found = data.find(item => item.id === parseInt(id));
            if (found) {
              const resolvedUrl =
                found.video_url ||
                found.stream_url ||
                found.youtube_embed ||
                null;
              setVideoData({ ...found, video_url: resolvedUrl });
              if (found.image) sessionStorage.setItem(`video_${id}_image`, found.image);
              return;
            }
          } catch (_) {}
        }
      } catch (error) {
        console.error('❌ Error fetching video:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideoData();
  }, [type, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  if (!videoData || !videoData.video_url) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-white text-2xl mb-4">Video not found</h2>
          <p className="text-gray-400 mb-4">Type: {type}, ID: {id}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between px-8 py-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-white hover:text-gray-300 transition"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-lg font-semibold">Back</span>
          </button>
          <h1 className="absolute left-1/2 transform -translate-x-1/2 text-white text-2xl font-bold truncate max-w-xl">
            {videoData.title}
          </h1>
          <div className="w-20"></div>
        </div>
      </div>

      <VideoPlayer
        videoUrl={videoData.video_url}
        title={videoData.title}
        autoPlay={true}
      />
    </div>
  );
};

export default Watch;